<template>
  <section v-if="settings && pages" class="z-grid v-page-section-container-padding-wide-screens container col-span-full mb-8 md:mb-12">
    <div class="z-grid__cols z-grid__content relative z-10 bg-white" :class="{ 'rounded-3xl pt-8': roundedFull, 'rounded-b-3xl sm:rounded-3xl': !roundedFull }">
      <transition name="fade">
        <FormKit
          v-show="!(settings.submitActionFormHide && submissionState === 'ok')"
          v-slot="{ state: { loading } }"
          type="form"
          :actions="false"
          :value="normalizedInitialValue"
          @submit="submit"
          @submit-invalid="scrollToErrors"
        >
          <AtomHeadingContentItem v-if="settings.displayFormTitle" :title="title" text-color="text-black" />

          <!-- Pages -->
          <ul>
            <li v-for="(page, pageIndex) of pages" v-show="pageIndex + 1 === currentPage" :key="page.id" class="mb-12">
              <h3 v-if="settings.displayCurrentPageTitle" class="text-bold text-lg">{{ page.name }}</h3>

              <!--
               Note that the following element is a copy from FormieRows.vue, we need this here because otherwise
               FormKit does not properly propagate the initial value, when given
               -->
              <ul>
                <li v-for="row of page.rows" v-show="rowIsVisible(row)" :key="row.id" class="mb-4">
                  <ul
                    :class="{
                      'md:grid md:gap-6': row.fields.length > 1,
                      'md:grid-cols-2': row.fields.length === 2,
                      'md:grid-cols-3': row.fields.length === 3,
                      'md:grid-cols-4': row.fields.length === 4,
                      'md:grid-cols-5': row.fields.length === 5,
                      'md:grid-cols-6': row.fields.length === 6,
                    }"
                  >
                    <li v-for="field of row.fields" :key="field.id">
                      <component :is="fieldComponentName(field)" :settings="settings" :field="field" />
                    </li>
                  </ul>
                </li>
                <li class="mb-4">
                  <div v-if="settings.hasRecaptcha" class="mb-4">
                    <MoleculeRecaptchaDisclaimer />
                  </div>

                  <template v-if="pages.length > 1">
                    <FormKit
                      v-show="pageIndex < pages.length - 1"
                      :disabled="loading || submissionState === 'ok'"
                      type="button"
                      :label="page.settings.submitButtonLabel"
                      class="inline-block"
                      @click="currentPage++"
                    />
                    <FormKit
                      v-show="pageIndex > 0"
                      :disabled="loading || submissionState === 'ok'"
                      type="button"
                      :label="page.settings.backButtonLabel"
                      class="inline-block"
                      @click="currentPage--"
                    />
                  </template>
                  <FormKit v-show="pageIndex === pages.length - 1" :disabled="loading || submissionState === 'ok'" type="submit">
                    <!-- todo: should not use AtomIcon here once we move this Formie code into a library -->
                    <AtomIcon v-show="loading && settings.loadingIndicator" icon="app-loader" class="mr-2 w-5 h-5 text-white" />
                    <span v-show="loading && settings.loadingIndicator && settings.loadingIndicatorText" v-html="settings.loadingIndicatorText" />
                    <span v-show="!(loading && settings.loadingIndicator)">{{ page.settings.submitButtonLabel }}</span>
                  </FormKit>
                </li>
              </ul>
            </li>
          </ul>
        </FormKit>
      </transition>

      <div v-show="submissionState === 'ok'" v-html="settings.submitActionMessageHtml" />
      <div v-show="submissionState === 'error'" class="mb-5" v-html="settings.errorMessageHtml" />
    </div>
  </section>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'
import flatten from 'lodash/flatten'
import type { FormKitNode } from '@formkit/core'
import { getValidationMessages } from '@formkit/validation'
import { fieldComponentName, fieldVisible } from '~/assets/formie'
import { linkDetails } from '~/assets/link'

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    // `readAsDataURL` always resolves to a string; the ArrayBuffer branch is for `readAsArrayBuffer`.
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/** A form value node whose key encodes a Formie field, as found by the walker in `postProcess`. */
type FormieValueMatch = { key: string; node: CmsData; type: string; group?: string; handle: string }

//
// Change FormKit's `value` into a Formie value
//
async function postProcess(fields: CmsData[], value: CmsData) {
  // A recursive finder that locates nodes eligible for post-processing
  function finder(root: unknown) {
    function helper(node: unknown, path: (string | number)[]) {
      if (node === undefined || node === null) {
        return
      }

      if (Array.isArray(node)) {
        for (let index = 0; index < node.length; index++) {
          // Apply recursion
          helper(node[index], [...path, index])
        }
        return
      }

      if (typeof node === 'object') {
        // Narrowed to `object` by the check above; the walker only ever visits plain form-value objects.
        const record = node as CmsData
        let match
        for (const [key, child] of Object.entries(record)) {
          // Apply recursion
          helper(child, [...path, key])

          match = key.match(/^__(?<type>Field_[a-z]+)(__(?<group>[a-z]+))?__(?<handle>[0-9a-z_]+)$/i)
          if (match?.groups) {
            const { type = '', group, handle = '' } = match.groups
            matches.push({ key, node: record, type, group, handle })
          }
        }
      }
    }

    const matches: FormieValueMatch[] = []
    helper(root, [])
    return matches
  }

  // Clone and find eligible nodes
  const clone = cloneDeep(value)
  const matches = finder(clone)

  // Make a map from the fields for easy access
  const fieldMap = Object.fromEntries([
    // Add every top-level field
    ...fields.map((field: CmsData) => [`__${field.__typename}__${field.handle}`, field]),
    // Add fields nested within Field_Group fields
    ...flatten(
      flatten(
        fields
          .filter((field: CmsData) => field.__typename === 'Field_Group')
          .map((field: CmsData) =>
            field.rows.map((row: CmsData) => row.fields.map((nestedField: CmsData) => [`__${nestedField.__typename}__${field.handle}__${nestedField.handle}`, nestedField])),
          ),
      ),
    ),
  ])

  for (const { key, node, type, handle } of matches) {
    switch (type) {
      case 'Field_Agree':
        // For FormKit this is a boolean, while Formie expects a non-empty string for 'true' and an empty string for 'false'
        node[handle] = node[key] ? fieldMap[key]?.checkedValue || 'True' : ''
        break

      case 'Field_Entries':
        // For FormKit this is either false or a string id, while Formie expects either null or an integer
        node[handle] = node[key] ? Number.parseInt(node[key]) : null
        break

      case 'Field_FileUpload': {
        // FormKit's file value is { name, file }[] (moved here from Formulate's :uploader callback,
        // which read+base64-encoded each file as it was selected - FormKit has no such hook, it just
        // hands back the raw File objects, so the same conversion now happens once, here, at submit).
        const files = Array.isArray(node[key]) ? node[key] : []
        node[handle] = await Promise.all(
          files
            .filter(({ file }: CmsData) => file)
            .map(async ({ name, file }: CmsData) => ({
              filename: name,
              fileData: await readFileAsDataUrl(file),
            })),
        )
        break
      }

      case 'Field_Hidden':
        // For FormKit this can be any type, while Formie expects null or a string
        node[handle] = node[key] ? `${node[key]}` : null
        break

      // Note: unlike Formulate, FormKit's group value is already a plain object (not array-wrapped), and
      // its list value is already a plain array - so Field_Group/Field_Name (useMultipleFields) need no
      // unwrapping here, only Field_Repeater (a FormKit 'list', one entry per row) still needs wrapping
      // into the { rows: [...] } shape Formie's mutation expects.

      case 'Field_Number':
        // For FormKit this is a string representing a number, while Formie expects a float, integer, or null
        if (node[key] && node[key].length) {
          if (Number.parseInt(fieldMap[key]?.decimals || '0')) {
            node[handle] = Number.parseFloat(node[key])
          } else {
            node[handle] = Number.parseInt(node[key])
          }
        } else {
          node[handle] = null
        }
        break

      case 'Field_Repeater':
        // For FormKit this is an array of group objects, while Formie expects an object with a property 'rows' that contains the array
        node[handle] = { rows: node[key] }
        break

      default:
        // All other values are unmodified
        node[handle] = node[key]
        break
    }
    delete node[key]
  }

  return clone
}

function setupForm(
  handle: string,
  { combinedSizeLimit, enableEntriesField, enableFileUploadField }: { combinedSizeLimit?: number; enableEntriesField: boolean; enableFileUploadField: boolean },
) {
  const graphqlFetch = useGraphqlFetch()

  const { data } = useAsyncData(`formie-form-${handle}`, async () => {
    const settingsFragment = `
      fragment settingsFragment on FormInterface {
        settings {
          displayFormTitle
          displayCurrentPageTitle
          defaultLabelPosition
          defaultInstructionsPosition

          errorMessageHtml
          redirectUrl
          submitAction
          submitActionFormHide
          submitActionMessageHtml
          submitActionMessageTimeout
          submitActionTab

          loadingIndicator
          loadingIndicatorText

          integrations {
            name
            handle
            enabled
            settings
          }
        }
      }`

    const captchasFragment = `
      fragment captchasFragment on FormInterface {
        captchas {
          handle
          name
          value
        }
      }`

    const fieldAgreeFragment = `
      fragment fieldAgreeFragment on Field_Agree {
        checkedValue
        defaultState
        name
        uncheckedValue
      }`

    const fieldCheckboxesFragment = `
      fragment fieldCheckboxesFragment on Field_Checkboxes {
        layout
        options {
          isDefault
          label
          value
        }
      }`

    const fieldDateFragment = `
      fragment fieldDateFragment on Field_Date {
        defaultDate
        includeTime
        minDate
        maxDate
      }`

    const fieldDropdownFragment = `
      fragment fieldDropdownFragment on Field_Dropdown {
        options {
          isDefault
          label
          value
        }
      }`

    const fieldEmailFragment = `
      fragment fieldEmailFragment on Field_Email {
        defaultValue
      }`

    const fieldEntriesFragment = `
      fragment fieldEntriesFragment on Field_Entries {
        entries {
          id
          title
        }
      }`

    const fieldFileUploadFragment = `
      fragment fieldFileUploadFragment on Field_FileUpload {
        allowedKinds
        limitFiles
        sizeLimit
        sizeMinLimit
        volumeHandle
      }`

    const fieldHeadingFragment = `
      fragment fieldHeadingFragment on Field_Heading {
        headingSize
      }`

    const fieldHiddenFragment = `
      fragment fieldHiddenFragment on Field_Hidden {
        defaultOption
        defaultValue
      }`

    const fieldHtmlFragment = `
      fragment fieldHtmlFragment on Field_Html {
        htmlContent
      }`

    const fieldMultiLineTextFragment = `
      fragment fieldMultiLineTextFragment on Field_MultiLineText {
        defaultValue
        minLength: min
        minType
        maxLength: max
        maxType
      }`

    const fieldNameFragment = `
      fragment fieldNameFragment on Field_Name {
        defaultValue
        useMultipleFields

        prefixEnabled
        prefixLabel
        prefixPlaceholder
        prefixDefaultValue
        prefixRequired
        prefixErrorMessage
        prefixOptions {
          label
          value
        }

        firstNameEnabled
        firstNameLabel
        firstNamePlaceholder
        firstNameDefaultValue
        firstNameRequired
        firstNameErrorMessage

        middleNameEnabled
        middleNameLabel
        middleNamePlaceholder
        middleNameDefaultValue
        middleNameRequired
        middleNameErrorMessage

        lastNameEnabled
        lastNameLabel
        lastNamePlaceholder
        lastNameDefaultValue
        lastNameRequired
        lastNameErrorMessage
      }`

    const fieldNumberFragment = `
      fragment fieldNumberFragment on Field_Number {
        defaultValue
        limit
        minValue: min
        maxValue: max
        decimals
      }`

    const fieldPhoneFragment = `
      fragment fieldPhoneFragment on Field_Phone {
        defaultValue
      }`

    const fieldRadioFragment = `
      fragment fieldRadioFragment on Field_Radio {
        layout
        options {
          isDefault
          label
          value
        }
      }`

    const fieldSectionFragment = `
      fragment fieldSectionFragment on Field_Section {
        borderStyle
        borderWidth
        borderColor
      }`

    const fieldSingleLineTextFragment = `
      fragment fieldSingleLineTextFragment on Field_SingleLineText {
        defaultValue
        minLength: min
        minType
        maxLength: max
        maxType
      }`

    const fieldProperties = `
      __typename
      id
      inputTypeName
      name
      handle
      labelPosition
      placeholder
      instructions
      instructionsPosition
      required
      errorMessage
      visibility`

    const fieldPropertyFragments = `
      ...fieldAgreeFragment
      ...fieldCheckboxesFragment
      ...fieldDateFragment
      ...fieldDropdownFragment
      ...fieldEmailFragment
      ${enableEntriesField ? '...fieldEntriesFragment' : ''}
      ${enableFileUploadField ? '...fieldFileUploadFragment' : ''}
      ...fieldHeadingFragment
      ...fieldHiddenFragment
      ...fieldHtmlFragment
      ...fieldMultiLineTextFragment
      ...fieldNameFragment
      ...fieldNumberFragment
      ...fieldPhoneFragment
      ...fieldRadioFragment
      ...fieldSectionFragment
      ...fieldSingleLineTextFragment`

    const pagesFragment = `
      fragment pagesFragment on FormInterface {
        title
        pages {
          id
          name
          settings {
            backButtonLabel
            buttonsPosition
            submitButtonLabel
          }
          rows {
            id
            fields: rowFields {
              ${fieldProperties}
              ${fieldPropertyFragments}
              ... on Field_Group {
                rows: nestedRows {
                  fields: rowFields {
                    ${fieldProperties}
                    ${fieldPropertyFragments}
                  }
                }
              }
              ... on Field_Repeater {
                addLabel
                minRows
                maxRows
                rows: nestedRows {
                  fields: rowFields {
                    ${fieldProperties}
                    ${fieldPropertyFragments}
                  }
                }
              }
            }
          }
        }
      }`

    const query = `
      query setupForm($handle: [String]) {
        formieForm(handle: $handle) {
          ...settingsFragment
          ...captchasFragment
          ...pagesFragment
        }
      }
      ${settingsFragment}
      ${captchasFragment}
      ${pagesFragment}
      ${fieldAgreeFragment}
      ${fieldCheckboxesFragment}
      ${fieldDateFragment}
      ${fieldDropdownFragment}
      ${fieldEmailFragment}
      ${enableEntriesField ? fieldEntriesFragment : ''}
      ${enableFileUploadField ? fieldFileUploadFragment : ''}
      ${fieldHeadingFragment}
      ${fieldHiddenFragment}
      ${fieldHtmlFragment}
      ${fieldMultiLineTextFragment}
      ${fieldNameFragment}
      ${fieldNumberFragment}
      ${fieldPhoneFragment}
      ${fieldRadioFragment}
      ${fieldSectionFragment}
      ${fieldSingleLineTextFragment}`

    const result = await graphqlFetch({ query, variables: { handle }, token: 'formie' })

    // Returned (not side-effect-assigned to refs declared outside this handler): useAsyncData only
    // transfers the server's SSR fetch to the client via what the handler *returns*. Mutating outer refs
    // as a side effect instead means the client - which reuses the transferred result and never re-runs
    // this handler - never sees those mutations, and stays stuck on their initial empty values forever.
    return {
      title: result.formieForm.title,
      settings: {
        ...result.formieForm.settings,
        hasRecaptcha: result.formieForm.settings.integrations?.some(({ handle, enabled }: CmsData) => handle === 'recaptcha' && enabled),
        combinedSizeLimit,
      },
      pages: result.formieForm.pages,
      captchas: result.formieForm.captchas,
    }
  })

  return {
    title: computed(() => data.value?.title),
    settings: computed<CmsData>(() => data.value?.settings || {}),
    pages: computed<CmsData[]>(() => data.value?.pages || []),
    captchas: computed<CmsData[]>(() => data.value?.captchas || []),
  }
}

function setupSubmit(handle: string, settings: Ref<CmsData>, pages: Ref<CmsData[]>, emit: (_event: 'preSubmission', _variables: CmsData) => void, captchas: Ref<CmsData[]>) {
  const router = useRouter()
  const config = useRuntimeConfig()
  const graphqlFetch = useGraphqlFetch()
  const submissionState = ref<'loading' | 'ok' | 'error' | undefined>(undefined)

  const submit = async (formData: CmsData) => {
    const { redirectUrl, submitActionTab, submitAction, hasRecaptcha } = unref(settings)
    submissionState.value = 'loading'

    const fields: CmsData[] = flatten(flatten(unref(pages).map((page: CmsData) => page.rows.map((row: CmsData) => row.fields))))
    const mutationName = `save_${handle}_Submission`

    try {
      const variables = await postProcess(fields, formData)

      if (hasRecaptcha) {
        const recaptchaCaptcha = unref(captchas)?.find(({ handle }: CmsData) => handle === 'recaptchaCaptcha')
        // The form says it has a recaptcha, so the CMS should have sent the captcha along with it. Fail
        // loudly here rather than on a property of `undefined` further down; either way the catch below
        // turns it into an 'error' submission state.
        if (!recaptchaCaptcha) {
          throw new Error(`Form "${handle}" has recaptcha enabled but the CMS returned no recaptchaCaptcha`)
        }
        const token = await getRecaptchaToken(recaptchaCaptcha.value)

        fields.push({
          handle: recaptchaCaptcha.handle,
          inputTypeName: 'FormieCaptchaInput',
        })

        variables.recaptchaCaptcha = {
          name: 'g-recaptcha-response',
          value: token,
        }
      }

      const mutationParameterDefinitions = fields.map((field) => `$${field.handle}: ${field.inputTypeName}`).join(', ')
      const mutationParameters = fields.map((field) => `${field.handle}: $${field.handle}`).join(', ')

      const query = `
            mutation saveSubmission(${mutationParameterDefinitions}) {
              ${mutationName}(${mutationParameters}) {
                uid
              }
        }`

      emit('preSubmission', variables)

      await graphqlFetch({ query, variables, token: 'formie' })

      submissionState.value = 'ok'

      const redirect = (targetUrl?: string) => {
        if (!targetUrl) {
          return
        }
        const rewrites = getLinkRewrites(config.public)
        const { scope, url } = linkDetails('//', targetUrl, { rewrites })
        if (submitActionTab === 'new-tab' || scope === 'outside') {
          window.open(url, submitActionTab === 'new-tab' ? '_blank' : '_self')
        } else {
          router.push({ path: url })
        }
      }
      switch (submitAction) {
        case 'reload':
          window.location.reload()
          break

        case 'entry':
        case 'url':
          redirect(redirectUrl)
          break
      }
    } catch (error) {
      console.error(error)
      submissionState.value = 'error'
    }
  }

  return {
    submissionState,
    submit,
  }
}

// Scrolls to the first field with a visible validation error on a failed submit attempt (FormKit's
// getValidationMessages walks the whole form tree collecting nodes that currently have one).
function scrollToErrors(node: FormKitNode) {
  const firstInvalidNode = getValidationMessages(node).keys().next().value
  if (firstInvalidNode?.props.id) {
    document.getElementById(firstInvalidNode.props.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

const props = defineProps({
  combinedSizeLimit: { type: Number, required: false, default: undefined },
  enableEntriesField: { type: Boolean, required: false, default: false },
  enableFileUploadField: { type: Boolean, required: false, default: true },
  roundedFull: { type: Boolean, required: false, default: false },
  handle: { type: String, required: true },
  initialValue: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
})

const emit = defineEmits(['preSubmission'])

const { title, settings, pages, captchas } = setupForm(props.handle, {
  combinedSizeLimit: props.combinedSizeLimit,
  enableEntriesField: props.enableEntriesField,
  enableFileUploadField: props.enableFileUploadField,
})
const { submissionState, submit } = setupSubmit(props.handle, settings, pages, emit, captchas)
const currentPage = ref(1)
const rowIsVisible = (row: CmsData) => row.fields.every(fieldVisible)

// todo: Normalize from {handle: value} -> {__Field_Hidden__handle: value}
const normalizedInitialValue = ref(cloneDeep(unref(props.initialValue)))
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  @apply transition duration-150 transition-opacity ease-in-out;
}

.fade-enter,
.fade-leave-to {
  @apply opacity-0;
}
</style>
